// The account's KB freshness rollup — `GET /v1/kb/status`.
//
// One row per ENABLED repo of the caller's account: how many pages its wiki
// holds, when it was last imported, when the gardener last ran over it, and
// whether background KB work is policy-enabled for that repo. A freshness
// table, so a stale repo reads stale ("3d") without SSH or psql.
//
// IT USED TO BE A SLICE OF `GET /v1/fleet/utilization` (`kb`), and it is its
// own read now because the table belongs to the REPOS page: the fleet
// snapshot is a health surface for whoever runs the fleet, and one account's
// repos' knowledge bases are not that. Nothing else read the field — the MCP
// `status` verb's fleet block is a `FleetSnapshot` with no `kb` member — so no
// other surface lost a column it had.
//
// The row's snake_case fields mirror the wire (`apps/api/src/routes/kb-status.ts`),
// like every other read in this app. This file is named after that route, in
// this directory's kebab-case convention (`scripts/file-naming.test.ts`).

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** One repo's KB freshness, as the wire spells it. */
export type KbStatusRepo = {
  readonly repo_id: string;
  readonly repo_full_name: string;
  readonly page_count: number;
  /** When the wiki was last imported, or null when it never was. */
  readonly last_import_iso: string | null;
  /** When the gardener last consolidated it, or null when it never ran. */
  readonly last_gardener_iso: string | null;
  /** Whether background KB work is policy-enabled for this repo. */
  readonly background_enabled: boolean;
};

/** The whole `GET /v1/kb/status` body: one row per enabled repo. */
export type KbStatusBody = {
  readonly repos: readonly KbStatusRepo[];
};

export const KB_STATUS_QUERY_KEY = 'kb-status';

/**
 * Fill a missing `repos` with an empty list, so every consumer can read
 * `data.repos` without a guard. Prod always returns the array; a stubbed or
 * partial body (tests, a stubbed mock) still resolves to a well-formed value
 * instead of crashing the panel on `undefined.length`.
 */
function normalize(r: Partial<KbStatusBody>): KbStatusBody {
  return { repos: r.repos ?? [] };
}

/**
 * The account's KB freshness rows. Idle with no session; keyed by the active
 * org so an org-switch re-reads under the new tenant.
 *
 * Polled, because the cells render an AGE ("3d"): a snapshot frozen at mount
 * drifts away from the truth it claims, and this is the one read on the page
 * whose value is a duration rather than a state.
 */
export function useKbStatus(token: string | null = SESSION_TOKEN) {
  return createQuery<KbStatusBody>(() => ({
    queryKey: [KB_STATUS_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    refetchInterval: 60_000,
    queryFn: async () => normalize(await apiFetch<Partial<KbStatusBody>>('/v1/kb/status', token)),
  }));
}
